// CSFCableCar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import '../src/app.css';

function CSFCableCar() {
  const [logs, setLogs] = useState([]);
  const [file, setFile] = useState(null);
  const [errorRows, setErrorRows] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // A ref to the log container for auto-scrolling
  const logContainerRef = useRef(null);

  // Scroll log container to bottom on update
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Helper function to log messages
  const addLog = (message) => {
    setLogs((prev) => [...prev, message]);
    console.log(message);
  };

  // File selection handler
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      addLog(`Selected file: ${selectedFile.name}`);
    }
  };

  // Clear logs and errors
  const clearLogs = () => {
    setLogs([]);
    setErrorRows([]);
    setShowErrorModal(false);
    addLog('Logs cleared.');
  };

  // Helper to capitalize the first letter (for day names)
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  // Download error CSV (including original line number)
  const downloadErrorCSV = () => {
    if (errorRows.length === 0) {
      addLog('No errors to download.');
      return;
    }
    // Unparse errorRows with columns: Line, Date, Day of Week, Count, Correct Day
    const csv = Papa.unparse(errorRows, {
      columns: ["Line", "Date", "Day of Week", "Count", "Correct Day"],
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "errors.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addLog('Error CSV downloaded.');
  };

  // Verify CSV data using Papa Parse
  const verifyData = () => {
    if (!file) {
      addLog('No CSV file selected.');
      return;
    }
    addLog('Starting verification...');
    setErrorRows([]); // reset errors

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const data = results.data;
        if (!data || data.length === 0) {
          addLog('File appears empty or has no data rows.');
          return;
        }
        
        // Define day names array (0: Sunday ... 6: Saturday)
        const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        let mismatchCount = 0;
        const errors = [];

        // Process each row. CSV header is line 1, so first row of data is line 2.
        data.forEach((row, index) => {
          const lineNumber = index + 2;
          const rawDateStr = row.Date ? row.Date.trim() : '';
          const rawDayStr = row["Day of Week"] ? row["Day of Week"].trim() : '';
          const count = row.Count ? row.Count : '';

          // If record has date "xxx xx, xxxx" and Day of Week "BLANK", ignore it.
          const cleanedDate = rawDateStr.replace(/^"|"$/g, '');
          if (cleanedDate === "xxx xx, xxxx" && rawDayStr.toUpperCase() === "BLANK") {
            return;
          }

          // Check if Date is missing
          if (!rawDateStr) {
            addLog(`Line ${lineNumber} error: Missing Date.`);
            errors.push({
              "Line": lineNumber,
              "Date": rawDateStr,
              "Day of Week": rawDayStr,
              "Count": count,
              "Correct Day": "Missing Date"
            });
            mismatchCount++;
            return;
          }

          // Check if Day of Week is missing (empty)
          if (!rawDayStr) {
            // Try to compute correct day if date is valid.
            const dateStr = rawDateStr.replace(/^"|"$/g, '');
            const parsedDate = new Date(dateStr);
            let correctDay = !isNaN(parsedDate.getTime())
              ? capitalize(daysOfWeek[parsedDate.getDay()])
              : "Invalid Date";
            addLog(`Line ${lineNumber} error: Day of Week is empty.`);
            errors.push({
              "Line": lineNumber,
              "Date": rawDateStr,
              "Day of Week": rawDayStr,
              "Count": count,
              "Correct Day": correctDay
            });
            mismatchCount++;
            return;
          }

          // Clean up date string and parse
          const dateStr = rawDateStr.replace(/^"|"$/g, '');
          const parsedDate = new Date(dateStr);
          if (isNaN(parsedDate.getTime())) {
            addLog(`Line ${lineNumber}: Invalid date format: "${rawDateStr}"`);
            errors.push({
              "Line": lineNumber,
              "Date": rawDateStr,
              "Day of Week": rawDayStr,
              "Count": count,
              "Correct Day": "Invalid Date"
            });
            mismatchCount++;
            return;
          }

          // Determine correct day using getDay() from the Date object.
          const actualDay = daysOfWeek[parsedDate.getDay()]; // returns lowercase day
          if (actualDay !== rawDayStr.toLowerCase()) {
            addLog(`Line ${lineNumber} MISMATCH: Date "${rawDateStr}" is actually "${capitalize(actualDay)}" but CSV says "${rawDayStr}".`);
            errors.push({
              "Line": lineNumber,
              "Date": rawDateStr,
              "Day of Week": rawDayStr,
              "Count": count,
              "Correct Day": capitalize(actualDay)
            });
            mismatchCount++;
          }
        });

        // Log summary as specified
        addLog('Verification complete.');
        addLog(`Total lines verified: ${data.length}`);
        addLog(`Total mismatches: ${mismatchCount}`);

        if (mismatchCount > 0) {
          setErrorRows(errors);
          // Auto popup modal if errors exist.
          setShowErrorModal(true);
        }
      },
      error: function (err) {
        addLog(`Error parsing CSV: ${err}`);
      },
    });
  };

  return (
    <div className="container">
      {/* "Back to Home" link */}
     

      <h1 className="app-title">SF Cable Car Ticket Data Verification</h1>

      {/* File selection */}
      <div className="file-input-section">
        <label htmlFor="sfFileInput" className="file-label">
          Select a CSV File:
        </label>
        <input
          id="sfFileInput"
          className="file-input"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
        />
      </div>

      {/* Buttons for verification, clearing logs, and downloading error CSV */}
      <div className="buttons-container">
        <button className="action-button" onClick={verifyData}>
          Verify Data
        </button>
        <button className="action-button" onClick={clearLogs}>
          Clear Log
        </button>
        {errorRows.length > 0 && (
          <button className="action-button" onClick={downloadErrorCSV}>
            Download Error CSV
          </button>
        )}
      </div>

      {/* Log area */}
      <div className="log-container" style={{ marginTop: '20px' }}>
        <h2 className="log-title">Log</h2>
        <div className="log-messages" ref={logContainerRef}>
          {logs.map((log, i) => (
            <div key={i} className="log-entry">
              {log}
            </div>
          ))}
        </div>
      </div>

      {/* Error Dashboard Modal */}
      {showErrorModal && (
        <div className="error-modal-overlay">
          <div className="error-modal-content">
            <div className="error-modal-header">
              <h2>Errors</h2>
            </div>
            <div className="error-modal-body">
              <table className="error-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Date</th>
                    <th>Day of Week</th>
                    <th>Count</th>
                    <th>Correct Day</th>
                  </tr>
                </thead>
                <tbody>
                  {errorRows.map((error, idx) => (
                    <tr key={idx}>
                      <td>{error.Line}</td>
                      <td>{error.Date}</td>
                      <td>{error["Day of Week"]}</td>
                      <td>{error.Count}</td>
                      <td>{error["Correct Day"]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="error-modal-footer">
              <button
                className="error-modal-close-button"
                onClick={() => setShowErrorModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CSFCableCar;
